import type { ExecutionBoundary, TDT } from "@src/types";
import { sortBy } from "lodash-es";

export const getAccountingCoverageMetrics = (run: {
  metrics?: TDT.BacktestMetrics | null;
  live_metrics?: TDT.BacktestMetrics | null;
}): TDT.BacktestMetrics | null | undefined => run.live_metrics ?? run.metrics;

export const TRACKING_ENDED_LABEL = "Tracking ended";
export const TRACKING_ENDED_EXPLANATION = "Final PnL and venue exposure unknown";
export const OUTSIDE_EXECUTION_INTERVAL_LABEL = "Recorded outside the execution interval";
export const EXECUTION_INTERVAL_UNAVAILABLE =
  "Execution interval unavailable: outside-interval check not made";
export const NO_RECORDED_MOVEMENTS = "No recorded movements";
export const RECORDED_COLLATERAL_INTERPRETATION =
  "raw sum of recorded collateral, not a current value or a loss";
export const MISSING_RECORDED_COLLATERAL = "unknown (a recorded amount is missing)";
export const UNREPORTED_ACCOUNTING_COVERAGE = "not reported by these metrics";

export type ExecutionInterval = { start: string | null; end: string | null } | null;

export const resolvePositionExecutionInterval = (
  position: { execution_id?: number | null },
  context: {
    start_ts?: string | null;
    end_ts?: string | null;
    executionBoundaries?: ExecutionBoundary[];
    group?: boolean;
  },
): ExecutionInterval => {
  if (context.group || context.executionBoundaries?.length) {
    const boundary = context.executionBoundaries?.find(
      (entry) => entry.executionId === position.execution_id,
    );
    const timestamp = (seconds: number): string => {
      const wholeSeconds = Math.floor(seconds);
      const fraction = String(seconds).split(".")[1] ?? "0";
      return new Date(wholeSeconds * 1000).toISOString().replace(".000Z", `.${fraction}Z`);
    };
    return boundary
      ? {
          start: boundary.startTs ?? timestamp(boundary.startTime),
          end:
            boundary.endTs !== undefined
              ? boundary.endTs
              : boundary.endTime == null
                ? null
                : timestamp(boundary.endTime),
        }
      : null;
  }
  return context.start_ts ? { start: context.start_ts, end: context.end_ts ?? null } : null;
};

export const getTrackingEndedMovementTime = (movement: {
  movement_ts?: string | null;
  timestamp?: string | null;
  create_ts?: string | null;
}): string => movement.movement_ts || movement.timestamp || movement.create_ts || "";

const compareFactTimes = (left: string, right: string): number => {
  const seconds = Math.floor(Date.parse(left) / 1000) - Math.floor(Date.parse(right) / 1000);
  if (seconds !== 0) return seconds;
  const leftFraction = left.match(/\d\d:\d\d:\d\d\.(\d+)/)?.[1] ?? "";
  const rightFraction = right.match(/\d\d:\d\d:\d\d\.(\d+)/)?.[1] ?? "";
  const length = Math.max(leftFraction.length, rightFraction.length);
  return leftFraction.padEnd(length, "0").localeCompare(rightFraction.padEnd(length, "0"));
};

export const isFactOutsideExecutionInterval = (
  time: string,
  interval: ExecutionInterval,
): boolean =>
  interval != null &&
  ((interval.start != null && compareFactTimes(time, interval.start) < 0) ||
    (interval.end != null && compareFactTimes(time, interval.end) > 0));

export type TrackingEndedFactBounds = { from: number; to: number; upperExclusive: boolean };

export const isTrackingEndedFactChartEligible = (
  time: string,
  bounds: TrackingEndedFactBounds | null | undefined,
  interval: ExecutionInterval,
): boolean => {
  const timestamp = Date.parse(time) / 1000;
  return (
    bounds != null &&
    timestamp >= bounds.from &&
    (bounds.upperExclusive ? timestamp < bounds.to : timestamp <= bounds.to) &&
    !isFactOutsideExecutionInterval(time, interval)
  );
};

// Tracking ended is an operator attestation; final PnL and venue exposure remain unknown.
interface PositionStatusInput {
  status_name?: string | null;
  end_ts?: string | null;
}

export const isPositionTrackingEnded = (position: PositionStatusInput): boolean =>
  position.status_name === "tracking_ended";

export const hasOnlyTrackingEndedPositions = (
  positions: readonly PositionStatusInput[] | undefined,
): boolean => positions != null && positions.length > 0 && positions.every(isPositionTrackingEnded);

export const sortRunPositions = <P extends { id: number; execution_id?: number | null }>(
  positions: P[] | undefined,
): P[] => {
  const hasMultipleExecutions =
    new Set(positions?.map((position) => position.execution_id)).size > 1;
  return hasMultipleExecutions
    ? sortBy(
        positions,
        (position) => position.execution_id,
        (position) => position.id,
      )
    : sortBy(positions, (position) => position.id);
};

export const isPositionOpen = (position: PositionStatusInput): boolean =>
  position.end_ts == null && !isPositionTrackingEnded(position);

export const isPositionFinanciallyClosed = (position: PositionStatusInput): boolean =>
  position.end_ts != null && !isPositionTrackingEnded(position);
